import React, { useState, useEffect } from 'react';
import {
  MapPin, Clock, Navigation, ChevronRight, RotateCcw, Check, CircleDot, Map, ArrowLeft, X, Star, Share2,
  UtensilsCrossed, Trees, Store, Wine, Mountain, Compass, Bed, Music, Sun, Sandwich,
  PawPrint, Calendar, ShoppingBag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import RatingModal from './RatingModal';
import RouteMap from './RouteMap';
import { submitWineryRating, getWineryRating, saveVisitedWineries, logWineryEvent } from '@/lib/api';

// Quick feature icons for stop cards — only the interesting/differentiating ones
const STOP_FEATURE_ICONS = {
  'Restaurant / Bistro': { icon: UtensilsCrossed, tip: 'Restaurant' },
  'Picnic Area':         { icon: Trees, tip: 'Picnic Area' },
  'Picnic Food Available': { icon: Sandwich, tip: 'Food Available' },
  'Wine Shop':           { icon: Store, tip: 'Wine Shop' },
  'Wine Club':           { icon: Wine, tip: 'Wine Club' },
  'Event Venue':         { icon: Calendar, tip: 'Events' },
  'Vineyard Views':      { icon: Mountain, tip: 'Views' },
  'Scenic Point':        { icon: Mountain, tip: 'Scenic' },
  'Tours':               { icon: Compass, tip: 'Tours' },
  'Accommodation':       { icon: Bed, tip: 'Stay' },
  'Live Music':          { icon: Music, tip: 'Live Music' },
  'Patio':               { icon: Sun, tip: 'Patio' },
  'Outdoor Seating':     { icon: Sun, tip: 'Outdoor' },
  'Pets Allowed':        { icon: PawPrint, tip: 'Pets OK' },
};

export default function RouteDisplay({ route, onReset, onSelectWinery, userLocation }) {
  // Track which stops have been visited or skipped
  const [visitedStops, setVisitedStops] = useState([]);
  const [skippedStops, setSkippedStops] = useState([]);
  const [showMapModal, setShowMapModal] = useState(false);
  const [ratingStop, setRatingStop] = useState(null); // Stop being rated
  const [ratingStopIndex, setRatingStopIndex] = useState(null);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  if (!route || !route.stops) {
    return null;
  }

  // Find the next unvisited, unskipped stop
  const nextStopIndex = route.stops.findIndex((_, index) => !visitedStops.includes(index) && !skippedStops.includes(index));

  const toggleVisited = (index) => {
    setVisitedStops(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Show rating modal when marking as done
  const handleMarkDone = (stop, index) => {
    logWineryEvent('done', stop);
    setRatingStop(stop);
    setRatingStopIndex(index);
  };

  // Skip a stop — session only, no localStorage write
  const handleSkip = (index) => {
    logWineryEvent('skip', route.stops[index]);
    setSkippedStops(prev => [...prev, index]);
  };

  const handleUnskip = (index) => {
    setSkippedStops(prev => prev.filter(i => i !== index));
  };

  // Handle rating submission
  const handleRatingSubmit = async (rating, comment) => {
    setIsSubmittingRating(true);
    try {
      await submitWineryRating(ratingStop.id, rating, comment);
    } catch (error) {
      console.error('Failed to submit rating:', error);
    } finally {
      setIsSubmittingRating(false);
      saveVisitedWineries([ratingStop]);
      toggleVisited(ratingStopIndex);
      setRatingStop(null);
      setRatingStopIndex(null);
    }
  };

  const handleShare = async () => {
    const stopIds = route.stops.map(s => s.id).filter(Boolean).join(',');
    const shareUrl = `https://okanaganwineroute.ca${stopIds ? `?stops=${stopIds}` : ''}`;
    const stopList = route.stops.map((s, i) => `${i + 1}. ${s.name}`).join('\n');
    const text = `🍷 ${route.route_name}\n\n${stopList}\n\nSee the full route & get directions:`;
    logWineryEvent('share', null, route.stops[0]?.region);
    if (navigator.share) {
      try {
        await navigator.share({ title: route.route_name, text, url: shareUrl });
      } catch (e) { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(`${text}\n${shareUrl}`);
    }
  };

  // Skip rating and just mark as visited
  const handleRatingSkip = () => {
    saveVisitedWineries([ratingStop]);
    toggleVisited(ratingStopIndex);
    setRatingStop(null);
    setRatingStopIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-3">
        <h2 className="text-xl font-bold text-stone-800">{route.route_name}</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMapModal(true)}
            className="text-[#722F37] border-[#722F37] hover:bg-[#722F37]/10 hover:border-[#C9A227]/50"
          >
            <Map className="w-4 h-4 mr-1" />
            View Map
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleShare}
            className="text-[#722F37] border-[#722F37] hover:bg-[#722F37]/10 hover:border-[#C9A227]/50"
          >
            <Share2 className="w-4 h-4 mr-1" />
            Share
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onReset}
            className="text-stone-600"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            New Route
          </Button>
        </div>
      </div>

      {/* Map Modal */}
      <AnimatePresence>
        {showMapModal && (
          <RouteMapModal
            stops={route.stops}
            routeName={route.route_name}
            onClose={() => setShowMapModal(false)}
            userLocation={userLocation}
          />
        )}
      </AnimatePresence>

      {/* Start Route Button - Goes to first stop */}
      <a
        href={getDirectionsToStop(route.stops[0])}
        target={isIOS ? '_self' : '_blank'}
        rel="noopener noreferrer"
        className="w-full h-14 bg-[#722F37] hover:bg-[#5a252c] text-lg text-white font-semibold rounded-md flex items-center justify-center ring-2 ring-[#C9A227]/30 shadow-lg"
      >
        <Navigation className="w-6 h-6 mr-2" />
        Start Route to {route.stops[0].name.split(' ').slice(0, 2).join(' ')}
      </a>

      {/* Progress indicator */}
      <div className="flex items-center justify-between text-sm text-stone-500 px-1">
        <span>{visitedStops.length} of {route.stops.length} visited</span>
        {visitedStops.length > 0 && (
          <button
            onClick={() => setVisitedStops([])}
            className="text-[#722F37] hover:underline"
          >
            Reset progress
          </button>
        )}
      </div>

      {/* Stops List */}
      <div className="space-y-3">
        {route.stops.map((stop, index) => (
          <div key={stop.name || index}>
            <StopCard
              stop={stop}
              number={index + 1}
              isLast={index === route.stops.length - 1}
              onClick={() => { logWineryEvent('view_detail', stop); onSelectWinery(stop); }}
              isVisited={visitedStops.includes(index)}
              isSkipped={skippedStops.includes(index)}
              isNext={index === nextStopIndex}
              onMarkDone={() => handleMarkDone(stop, index)}
              onMarkNotVisited={() => toggleVisited(index)}
              onSkip={() => handleSkip(index)}
              onUnskip={() => handleUnskip(index)}
            />
          </div>
        ))}
      </div>

      {/* Rating Modal */}
      <AnimatePresence>
        {ratingStop && (
          <RatingModal
            winery={ratingStop}
            onSubmit={handleRatingSubmit}
            onSkip={handleRatingSkip}
            isSubmitting={isSubmittingRating}
          />
        )}
      </AnimatePresence>

      {/* Tips */}
      {(() => {
        const region = route.stops[0]?.region;
        const REGION_TIPS = {
          'Oliver / Golden Mile Bench': { label: 'Oliver Tip', text: 'The District Wine Village at 100 Enterprise Way has 8 boutique wineries, a brewery, a distillery, an artisan bakery, specialty coffee, a bookstore, and a restaurant — all in one spot. Worth checking out for a laid-back day with friends and family.' },
          'Osoyoos / Black Sage': { label: 'Osoyoos Tip', text: "You're in Canada's only genuine desert. Osoyoos Lake is the warmest freshwater lake in Canada — bring your swimsuit. Black Sage Road is one of the most scenic wine drives in the valley." },
          'Penticton': { label: 'Penticton Tip', text: "Penticton sits between two lakes — Okanagan to the north and Skaha to the south. The Saturday farmers market downtown is worth a stop if you're here on a weekend." },
          'Naramata': { label: 'Naramata Tip', text: 'The Naramata Bench winds along the east side of Okanagan Lake with beautiful lake views between every winery. No gas stations or grocery stores on the bench — fill up and grab snacks in Penticton before heading out.' },
          'Summerland': { label: 'Summerland Tip', text: 'Summerland is the home of the Bottleneck Drive wine trail. The Kettle Valley Railway trail is right nearby — great for a walk or bike ride between tastings.' },
          'Kelowna': { label: 'Kelowna Tip', text: "Kelowna sits on the east shore of Okanagan Lake, home to the legendary Ogopogo sea creature. Keep an eye on the water between tastings — locals swear by it." },
          'West Kelowna': { label: 'West Kelowna Tip', text: 'Most of the wineries here are clustered along Boucherie Road and the Westside Wine Trail — easy to hop between stops with great views of Okanagan Lake.' },
          'Lake Country': { label: 'Lake Country Tip', text: 'The Okanagan Rail Trail runs right through this area — a converted railway corridor perfect for a walk or bike ride with wineries along the way.' },
          'OK Falls': { label: 'OK Falls Tip', text: "Okanagan Falls is a quiet little village on Skaha Lake. Tickleberry's just up the road is famous for its homemade ice cream — a great way to end the day." },
          'Kaleden': { label: 'Kaleden Tip', text: "Kaleden is one of the valley's hidden gems — a tiny community above Skaha Lake with a handful of boutique wineries and stunning views. Expect a peaceful, unhurried experience." },
          'Keremeos': { label: 'Keremeos Tip', text: "Keremeos calls itself the Fruit Stand Capital of Canada — the roadside stands along Highway 3 are hard to drive past. The Grist Mill & Gardens is a restored 1877 flour mill worth a quick visit." },
          'Skaha Bench': { label: 'Skaha Bench Tip', text: "Skaha Bench is one of the valley's newest and fastest-growing wine areas, perched above Skaha Lake between Penticton and OK Falls. Smaller crowds and some real finds here." },
          'Peachland': { label: 'Peachland Tip', text: 'Peachland is a charming lakeside village with a walkable Beach Avenue strip. Hardy Falls Regional Park is a short hike nearby — a nice complement to a wine day.' },
        };
        const tip = REGION_TIPS[region];
        return (
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
            <p className="text-sm text-amber-800">
              {tip ? (
                <><strong>💡 {tip.label}:</strong> {tip.text}</>
              ) : (
                <><strong>Pro tip:</strong> Call ahead to confirm hours and make reservations for tastings.</>
              )}
            </p>
          </div>
        );
      })()}
    </div>
  );
}

function StopCard({ stop, number, isLast, onClick, isVisited, isSkipped, isNext, onMarkDone, onMarkNotVisited, onSkip, onUnskip }) {
  const directionsUrl = getDirectionsToStop(stop);
  const [rating, setRating] = useState({ average: null, count: 0 });

  // Fetch rating for this winery
  useEffect(() => {
    if (stop?.id) {
      getWineryRating(stop.id).then(setRating);
    }
  }, [stop?.id]);

  return (
    <div className="relative">
      <div className={cn(
        "bg-white rounded-xl border shadow-sm transition-all",
        isNext && !isVisited && !isSkipped && "border-[#722F37] border-2 shadow-md",
        isVisited && "border-green-200 bg-green-50/50",
        isSkipped && "border-stone-200 bg-stone-50/50 opacity-60",
        !isNext && !isVisited && !isSkipped && "border-stone-300"
      )}>
        {/* Next stop indicator */}
        {isNext && !isVisited && !isSkipped && (
          <div className="bg-[#722F37] text-white text-xs font-semibold px-3 py-1 rounded-t-lg flex items-center gap-1">
            <CircleDot className="w-3 h-3" />
            NEXT STOP
          </div>
        )}

        <div
          onClick={onClick}
          className={cn(
            "flex gap-4 p-4 cursor-pointer hover:bg-stone-50 transition-colors",
            isNext && !isVisited ? "rounded-b-xl" : "rounded-t-xl"
          )}
        >
          {/* Number Badge */}
          <div className="flex-shrink-0">
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center font-bold text-white",
              isVisited
                ? "bg-green-500"
                : "bg-gradient-to-br from-[#722F37] to-[#8B3A42]"
            )}>
              {isVisited ? <Check className="w-5 h-5" /> : number}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className={cn(
              "font-semibold truncate",
              isVisited ? "text-stone-500 line-through" : "text-stone-800"
            )}>{stop.name}</h3>
            <div className="flex items-center gap-1 text-stone-500 text-sm mt-1">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{stop.address}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(stop.restaurant_open || (stop.food_service && (stop.food_service !== 'Food Truck' || [0, 6].includes(new Date().getDay())))) && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  <UtensilsCrossed className="w-3 h-3" />
                  {stop.food_service || 'Dining'}
                </span>
              )}
            </div>
            {/* Feature Icons */}
            {stop.features?.length > 0 && (() => {
              const icons = stop.features
                .map(f => STOP_FEATURE_ICONS[f])
                .filter(Boolean)
                .slice(0, 5); // Show max 5 icons
              if (icons.length === 0) return null;
              return (
                <div className="flex items-center gap-2 mt-2">
                  {icons.map(({ icon: Icon, tip }) => (
                    <span key={tip} title={tip} className="text-stone-400">
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                  ))}
                </div>
              );
            })()}
            {/* Rating Display */}
            {rating.count > 0 && (
              <div className="flex items-center gap-1 mt-2">
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={cn(
                        "w-3 h-3",
                        star <= Math.round(rating.average)
                          ? "fill-amber-400 text-amber-400"
                          : "text-stone-300"
                      )}
                    />
                  ))}
                </div>
                <span className="text-xs text-stone-500">
                  {rating.average.toFixed(1)} ({rating.count})
                </span>
              </div>
            )}
          </div>

          {/* Info Icon */}
          <div className="flex-shrink-0 flex items-center">
            <ChevronRight className="w-5 h-5 text-stone-400" />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 p-3 pt-0">
          {isVisited ? (
            <button
              onClick={(e) => { e.stopPropagation(); onMarkNotVisited(); }}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 border border-stone-300 text-stone-500 text-sm font-medium rounded-lg hover:bg-stone-50 transition-colors"
            >
              Mark as not visited
            </button>
          ) : isSkipped ? (
            <button
              onClick={(e) => { e.stopPropagation(); onUnskip(); }}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 border border-stone-300 text-stone-500 text-sm font-medium rounded-lg hover:bg-stone-50 transition-colors"
            >
              Undo skip
            </button>
          ) : (
            <>
              <a
                href={directionsUrl}
                target={isIOS ? '_self' : '_blank'}
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-[#722F37] text-white text-sm font-medium rounded-lg hover:bg-[#5a252c] transition-colors ring-1 ring-[#C9A227]/30"
              >
                <Navigation className="w-4 h-4" />
                Navigate
              </a>
              <button
                onClick={(e) => { e.stopPropagation(); onSkip(); }}
                className="flex items-center justify-center gap-1.5 py-2 px-3 border border-stone-300 text-stone-500 text-sm font-medium rounded-lg hover:bg-stone-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Skip
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onMarkDone(); }}
                className="flex items-center justify-center gap-1.5 py-2 px-3 border border-green-500 text-green-600 text-sm font-medium rounded-lg hover:bg-green-50 transition-colors"
              >
                <Check className="w-4 h-4" />
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RouteMapModal({ stops, routeName, onClose, userLocation }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="absolute inset-0 bg-white flex flex-col"
      >
        {/* Header with Back Button */}
        <div className="flex-shrink-0 bg-white border-b border-stone-200 px-4 py-3 flex items-center gap-3 safe-area-top">
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#722F37] hover:bg-[#5a252c] transition-colors text-white font-semibold text-base ring-1 ring-[#C9A227]/30"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Route
          </button>
          <div className="flex-1">
            <h2 className="font-semibold text-stone-800 truncate">{routeName}</h2>
            <p className="text-sm text-stone-500">{stops.length} stops</p>
          </div>
        </div>

        {/* Leaflet Map */}
        <div className="flex-1 relative">
          <RouteMap stops={stops} userLocation={userLocation} />
        </div>
      </motion.div>
    </motion.div>
  );
}

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

function getDirectionsToStop(stop) {
  const address = encodeURIComponent(stop.address || stop.name);
  if (isIOS) {
    return `https://maps.apple.com/?saddr=Current+Location&daddr=${address}&dirflg=d`;
  } else {
    return `https://www.google.com/maps/dir/?api=1&destination=${address}&travelmode=driving`;
  }
}

